//common client and server utils

export const CUSTOM_PRICING_USAGE_CAP_USD = 10_000;

export const FREE_TRIAL_DAYS = 7;

export const BUY_ONE_TIME_ADD_ON_INTENT = "buyOneTimeAddOn";
export const CANCEL_SUBSCRIPTION_INTENT = "cancel";
export const CUSTOM_CHANGE_PLAN_INTENT = "customChangePlan";
export const CUSTOM_SUBSCRIBE_INTENT = "customSubscribe";
export const SUBSCRIBE_INTENT = "subscribe";
export const VALIDATE_COUPON_CODE_INTENT = "validateCoupon";

export enum EPlanId {
  FREE = "FREE",
  BASIC = "BASIC",
  BASIC_YEARLY = "BASIC_YEARLY",
  GROW = "GROW",
  GROW_YEARLY = "GROW_YEARLY",
  PLUS = "PLUS",
  PLUS_YEARLY = "PLUS_YEARLY",
  CUSTOM = "CUSTOM",
}

export type IPlanId = `${EPlanId}`;

export interface IPlan {
  id: IPlanId;
  displayName: string;
  price: number;
  parentPlanId?: IPlanId;
  isYearly?: boolean;
  isUsageBased?: boolean;
  usagePercentage?: number; //percentage of revenue to charge usage
  usageChargeStartLimit?: number; //when to charge usage USD
  usageCappedAmount?: number; //max amount to charge usage USD
  isHidden?: boolean;
  isCustomUsageOnly?: boolean;
}

export const PLANS: Record<IPlanId, IPlan> = {
  FREE: {
    id: EPlanId.FREE,
    displayName: "Free",
    price: 0,
  },
  BASIC: {
    id: EPlanId.BASIC,
    displayName: "Basic",
    price: 14.99,
  },
  BASIC_YEARLY: {
    id: EPlanId.BASIC_YEARLY,
    displayName: "Basic (Yearly)",
    price: 134.99, //3 months free
    parentPlanId: EPlanId.BASIC,
    isYearly: true,
  },
  GROW: {
    id: EPlanId.GROW,
    displayName: "Grow",
    price: 19.99,
    isUsageBased: true,
    usagePercentage: 0.5,
    usageChargeStartLimit: 25000,
    usageCappedAmount: 500,
    isHidden: true,
  },
  GROW_YEARLY: {
    id: EPlanId.GROW_YEARLY,
    displayName: "Grow (Yearly)",
    price: 179.99, //3 months free
    parentPlanId: EPlanId.GROW,
    isYearly: true,
    isHidden: true,
  },
  PLUS: {
    id: EPlanId.PLUS,
    displayName: "Plus",
    price: 39.99,
    isUsageBased: true,
    usagePercentage: 0.5,
    usageChargeStartLimit: 50000,
    usageCappedAmount: 500,
  },
  PLUS_YEARLY: {
    id: EPlanId.PLUS_YEARLY,
    displayName: "Plus (Yearly)",
    price: 359.99, //3 months free
    parentPlanId: EPlanId.PLUS,
    isYearly: true,
    isHidden: true,
  },
  CUSTOM: {
    id: EPlanId.CUSTOM,
    displayName: "Custom",
    price: 0,
    isUsageBased: true,
    usageCappedAmount: CUSTOM_PRICING_USAGE_CAP_USD,
    isCustomUsageOnly: true,
    isHidden: true,
  },
};

export enum EOneTimeAddOnIds {
  CONCIERGE_SERVICE = "CONCIERGE_SERVICE",
}

export type IOneTimeAddOnId = `${EOneTimeAddOnIds}`;

export type IOneTimeAddOnDetails = {
  id: IOneTimeAddOnId;
  displayName: string;
  price: number; //in USD
};

export const ONE_TIME_ADD_ONS: Record<IOneTimeAddOnId, IOneTimeAddOnDetails> = {
  CONCIERGE_SERVICE: {
    id: EOneTimeAddOnIds.CONCIERGE_SERVICE,
    displayName: "Concierge Service Add-on", //should be unique as this will be used as identifer in webhook!
    price: 99,
  },
};

export function getAllPlans(): IPlan[] {
  return Object.values(PLANS);
}

/** Plans shown on the pricing UI: hidden plans are omitted unless the shop is already on that plan. */
export function getPlansVisibleForPricing(
  currentPlan: IPlan,
  variantPlans?: IPlan[],
): IPlan[] {
  const plans = variantPlans || getAllPlans();
  return plans.filter((p) => !p.isHidden || p.id === currentPlan.id);
}

//if user is specifically on shopify plus plan (not advanced etc.)
export function getIsPlusUser(shopifyPlanPublicName?: string): boolean {
  if (!shopifyPlanPublicName) {
    return false;
  }

  const normalizedName = shopifyPlanPublicName.toLowerCase().trim();
  return normalizedName.includes("plus");
}

export function getIsDevStore(
  plan_partnerDevelopment: boolean,
  plan_publicDisplayName: string,
): boolean {
  if (plan_partnerDevelopment) return true;
  const normalizedName = plan_publicDisplayName?.trim().toLowerCase() ?? "";
  return normalizedName.includes("develop");
}

export function getIsUserOnShopifyPlusPlan(
  shopifyPlanPublicName?: string,
): boolean {
  if (!shopifyPlanPublicName) {
    return false;
  }

  const normalizedName = shopifyPlanPublicName.toLowerCase().trim();
  return normalizedName.includes("plus");
}

export function isAdvancedOrPlusShopifyPlan(planName?: string | null): boolean {
  const normalizedName = planName?.trim().toLowerCase() ?? "";
  return (
    normalizedName.includes("plus") ||
    normalizedName.includes("advanced") ||
    normalizedName.includes("retail") ||
    normalizedName.includes("shop component") ||
    normalizedName.includes("enterprise")
  );
}

export function getRequiredParentPlan(shopifyPlanPublicName: string): IPlan {
  const normalizedName = shopifyPlanPublicName.toLowerCase().trim();

  if (normalizedName.includes("develop")) {
    return PLANS.FREE;
  }

  if (normalizedName.includes("basic")) {
    return PLANS.BASIC;
  }

  if (isAdvancedOrPlusShopifyPlan(normalizedName)) {
    return PLANS.PLUS;
  }

  //disable grow plan, now all just require basic
  // if (
  //   normalizedName.includes("grow") ||
  //   normalizedName.includes("shopify") ||
  //   normalizedName.includes("other")
  // ) {
  //   return PLANS.GROW;
  // }

  return PLANS.BASIC;
}

/** Flat percent off the base subscription price for the early-user promo code below. */
export const EARLY_USER_COUPON_DISCOUNT_PERCENT = 20;

/**
 * Deterministic subscribe-page promo code for early users — derived purely from
 * the shop domain (e.g. `db-gg.myshopify.com` -> `DB-GG-SPECIAL`), never stored
 * in the database. Safe to compute on the client to display/copy, and re-derived
 * server-side to validate a submitted code.
 */
export function generateEarlyUserCouponCode(myshopifyDomain: string): string {
  const shopHandle = myshopifyDomain.trim().replace(/\.myshopify\.com$/i, "");
  return `${shopHandle.toUpperCase()}-SPECIAL`;
}

/**
 * Legacy paid plans can move directly to CUSTOM pricing when Shopify now
 * requires the deprecated Plus tier. Merchants who are already subscribed to
 * Plus remain on the legacy pricing page so their grandfathered subscription
 * is left untouched.
 */
export function isLegacyPlanCustomPricingMigration(params: {
  activePlanId?: string | null;
  shopifyPlanPublicName?: string | null;
}): boolean {
  const activePlan = params.activePlanId
    ? PLANS[params.activePlanId as IPlanId]
    : null;
  if (
    !activePlan ||
    activePlan.id === EPlanId.FREE ||
    activePlan.id === EPlanId.CUSTOM
  ) {
    return false;
  }

  const activeParentPlan = activePlan.parentPlanId
    ? PLANS[activePlan.parentPlanId]
    : activePlan;
  const requiredPlan = getRequiredParentPlan(
    params.shopifyPlanPublicName ?? "",
  );

  return (
    requiredPlan.id === EPlanId.PLUS &&
    activeParentPlan.id !== EPlanId.PLUS &&
    activeParentPlan.price < requiredPlan.price
  );
}
